import { useState, useCallback, useRef, useEffect } from 'react';

export interface Toast {
  id: string;
  message: string;
  variant: 'error' | 'info' | 'success' | 'warning';
  duration?: number;
  priority?: number;
  pausedAt?: number;
  createdAt: number;
  key?: string;
}

interface ToastQueueOptions {
  maxToasts?: number;
  defaultDuration?: number;
  position?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right';
}

const PRIORITY_MAP = {
  error: 4,
  warning: 3,
  success: 2,
  info: 1,
};

/**
 * Hook para gestionar cola de toasts con priorización y auto-dismiss
 *
 * Features:
 * - Cola con límite configurable (default: 3)
 * - Duración dinámica basada en longitud de mensaje
 * - Priorización automática (error > warning > success > info)
 * - Pause on hover
 * - Auto-dismiss con timer
 *
 * @example
 * ```tsx
 * const queue = useToastQueue({ maxToasts: 3, position: 'bottom-right' });
 * queue.addToast('Operación exitosa', 'success');
 * ```
 */
export function useToastQueue(options: ToastQueueOptions = {}) {
  const { maxToasts = 3, defaultDuration = 3000, position = 'top-right' } = options;

  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  /**
   * Calcular duración dinámica basada en longitud del mensaje
   * Fórmula: 1 caracter = 100ms, mínimo 2s, máximo 6s
   */
  const calculateDuration = useCallback((message: string): number => {
    // return 10000; // for testing
    const charDuration = message.length * 140;
    return Math.min(Math.max(charDuration, 3000), 6000);
  }, []);

  /**
   * Agregar toast a la cola con priorización
   * @param options.key - Key opcional para reemplazar toasts previos con el mismo key
   * @param options.duration - Duración personalizada en ms
   */
  const addToast = useCallback(
    (
      message: string,
      variant: Toast['variant'],
      options?: { key?: string; duration?: number }
    ) => {
      const id = `toast-${Date.now()}-${Math.random()}`;
      const duration = options?.duration || calculateDuration(message);
      const priority = PRIORITY_MAP[variant];
      const key = options?.key;

      const newToast: Toast = {
        id,
        message,
        variant,
        duration,
        priority,
        createdAt: Date.now(),
        key,
      };

      setToasts((prevToasts) => {
        let filtered = prevToasts;

        // Si hay key, remover toasts previos con el mismo key
        if (key) {
          const toastsToRemove = prevToasts.filter((t) => t.key === key);

          // Limpiar timers de toasts removidos
          toastsToRemove.forEach((t) => {
            const timer = timersRef.current.get(t.id);
            if (timer) {
              clearTimeout(timer);
              timersRef.current.delete(t.id);
            }
          });

          filtered = prevToasts.filter((t) => t.key !== key);
        }

        // Si hay espacio, agregar directamente
        if (filtered.length < maxToasts) {
          return [...filtered, newToast];
        }

        // Si está lleno, remover el de menor prioridad si el nuevo es más importante
        const lowestPriorityToast = [...filtered].sort(
          (a, b) => (a.priority || 0) - (b.priority || 0)
        )[0];

        if ((newToast.priority || 0) > (lowestPriorityToast.priority || 0)) {
          // Remover toast de menor prioridad
          const finalFiltered = filtered.filter((t) => t.id !== lowestPriorityToast.id);
          return [...finalFiltered, newToast];
        }

        // Si no hay espacio y no es prioritario, ignorar
        console.warn('Toast queue full, message ignored:', message);
        return filtered;
      });

      // Auto-dismiss timer
      const timer = setTimeout(() => {
        removeToast(id);
      }, duration);

      timersRef.current.set(id, timer);

      return id;
    },
    [maxToasts, calculateDuration]
  );

  /**
   * Remover toast de la cola
   */
  const removeToast = useCallback((id: string) => {
    // Limpiar timer
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }

    // Remover de la cola
    setToasts((prevToasts) => prevToasts.filter((t) => t.id !== id));
  }, []);

  /**
   * Pausar auto-dismiss (on hover)
   */
  const pauseToast = useCallback((id: string) => {
    // Limpiar timer
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }

    // Marcar como pausado
    setToasts((prevToasts) =>
      prevToasts.map((t) => (t.id === id ? { ...t, pausedAt: Date.now() } : t))
    );
  }, []);

  /**
   * Reanudar auto-dismiss (on hover out)
   */
  const resumeToast = useCallback(
    (id: string) => {
      setToasts((prevToasts) => {
        const toast = prevToasts.find((t) => t.id === id);
        if (!toast || !toast.pausedAt || !toast.duration) {
          return prevToasts;
        }

        // Calcular tiempo restante
        const elapsed = Date.now() - toast.pausedAt;
        const remaining = toast.duration - elapsed;

        if (remaining > 0) {
          // Re-crear timer con tiempo restante
          const timer = setTimeout(() => {
            removeToast(id);
          }, remaining);

          timersRef.current.set(id, timer);
        } else {
          // Si ya expiró, remover inmediatamente
          removeToast(id);
        }

        // Quitar marca de pausa
        return prevToasts.map((t) => (t.id === id ? { ...t, pausedAt: undefined } : t));
      });
    },
    [removeToast]
  );

  /**
   * Limpiar todos los toasts
   */
  const clearAll = useCallback(() => {
    // Limpiar todos los timers
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current.clear();

    setToasts([]);
  }, []);

  /**
   * Helper para reemplazar toast con key
   * Elimina cualquier toast existente con el mismo key y agrega uno nuevo
   */
  const replaceToast = useCallback(
    (key: string, message: string, variant: Toast['variant'], duration?: number) => {
      return addToast(message, variant, { key, duration });
    },
    [addToast]
  );

  // Cleanup en unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();
    };
  }, []);

  return {
    toasts,
    addToast,
    removeToast,
    pauseToast,
    resumeToast,
    clearAll,
    replaceToast,
    position,
  };
}

export type UseToastQueue = ReturnType<typeof useToastQueue>;
